"use client";

// Registra el service worker y gestiona la suscripción a notificaciones push.
import { useEffect, useState } from "react";
import Icon from "./Icon";
import { api } from "../lib/api";

function urlBase64ToUint8Array(base64: string) {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const raw = atob((base64 + padding).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

export default function PushToggle() {
  const [estado, setEstado] = useState<"cargando" | "activo" | "inactivo" | "no-soportado">("cargando");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
      setEstado("no-soportado");
      return;
    }
    // El SW ya lo registra ServiceWorkerRegister desde el layout
    navigator.serviceWorker.ready
      .then((reg) => reg.pushManager.getSubscription())
      .then((sub) => setEstado(sub ? "activo" : "inactivo"))
      .catch(() => setEstado("no-soportado"));
  }, []);

  async function activar() {
    setError("");
    try {
      const permiso = await Notification.requestPermission();
      if (permiso !== "granted") {
        setError("Bloqueaste las notificaciones — habilitalas desde el candado del navegador");
        return;
      }
      const key = process.env.NEXT_PUBLIC_VAPID_KEY;
      if (!key) { setError("Push no está configurado en el servidor"); return; }
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(key),
      });
      const json = sub.toJSON() as { endpoint: string; keys: { p256dh: string; auth: string } };
      try {
        await api("/notifications/subscribe", {
          method: "POST",
          body: JSON.stringify({ endpoint: json.endpoint, keys: json.keys }),
        });
      } catch (e) {
        // Si el servidor no la registró, deshacemos la del navegador: si no,
        // al recargar diría "Activadas" y no llegaría ninguna notificación.
        await sub.unsubscribe().catch(() => {});
        throw e;
      }
      setEstado("activo");
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudieron activar");
    }
  }

  async function desactivar() {
    setError("");
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await api("/notifications/subscribe", {
          method: "DELETE",
          body: JSON.stringify({ endpoint: sub.endpoint }),
        }).catch(() => {});
        await sub.unsubscribe();
      }
      setEstado("inactivo");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Error");
    }
  }

  if (estado === "no-soportado") return null;

  return (
    <div className="rounded-xl bg-[#1A2123] border border-white/10 p-4 flex items-center gap-3">
      <div className="flex-1">
        <strong className="text-[14.5px]">Notificaciones push</strong>
        <div className="text-[12px] text-[#808D88]">
          Avisos de derivaciones, presupuestos, mensajes y pedidos aunque tengas la app cerrada.
        </div>
        {error && <div className="text-[12px] text-[#DE705C] mt-1">{error}</div>}
      </div>
      {estado === "cargando" ? (
        <span className="text-xs text-[#808D88]">…</span>
      ) : (
        <button
          onClick={estado === "activo" ? desactivar : activar}
          className={`inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-bold flex-none ${
            estado === "activo"
              ? "bg-[#5CBE8F]/15 text-[#5CBE8F]"
              : "bg-[#E8A23C] text-[#201404] hover:brightness-105"
          }`}
        >
          {estado === "activo" ? (
            <><Icon name="check" size={15} strokeWidth={2.5} /> Activadas</>
          ) : (
            <><Icon name="bell" size={15} /> Activar</>
          )}
        </button>
      )}
    </div>
  );
}
